/*
 *******************************************************************************
 * 
 *  Filename:   ./agent.js
 *
 *  Syntax:     NA
 *
 *  Synopsis:   Aggregates all Agents into a single export. Agents perform data
 *              transactions with the API server (get, post, put, delete).
 *  
 *  Author:     Norman J. Nolasco [ Advarion Incorporated ]
 *  
 *  Created:    Saturday, September 15, 2018 - 5:21 AM (CST)
 *  
 *  Notes:
 *      09/17/2018  NJN     Add Components
 *  
 *  
 *  Revisions:
 *      09/15/2018  NJN     File Created
 *      09/18/2018  NJN     Added Navs
 *      09/25/2018  NJN     Add BlockValidationTypeFields
 *      09/27/2018  NJN     Add BlockElementOptions, BlockElementTypes, BlockGroups
 *      09/30/2018  NJN     Add Blocks, BlockPath, BlockElements
 *      10/28/2018  NJN     Add BlockDatas
 *      11/14/2018  NJN     Add BlockFormats, BlockFormatLevels
 *      11/19/2018  NJN     Add Folders
 *      12/02/2018  NJN     Add FileTypes
 *      12/03/2018  NJN     Add Contacts
 *      12/19/2018  NJN     Add Transactions
 *      01/21/2019  NJN     Add Consequences
 *      01/21/2019  NJN     Add Probabilities
 *      01/21/2019  NJN     Add RiskClasses
 *      01/21/2019  NJN     Add RiskMatrix
 *      01/21/2019  NJN     Add Assets
 *      03/05/2019  NJN     Add Field Set Groups, FieldSets, Fields
 *      04/01/2019  NJN     Add GCSEquipments, Inspections
 *      05/13/2019  NJN     Add Location States, Countries, Counties, Zip Codes
 *      06/15/2019  NJN     Add Projects, Versions
 *      06/27/2019  NJN     Add Project_ProjectJobs
 *      07/06/2019  NJN     Add GoCampaign_PoliticalDistrictGroups, LocationCities
 *      07/10/2019  NJN     Add GoCampaign_PoliticalDistricts
 *      07/11/2019  NJN     Add GoCampaign_OfficeGroups
 *      09/29/2019  NJN     Add GoCampaign_PrecinctGroups, VoterDB_Voters
 *      10/26/2019  NJN     Add GoCampaign_Counties, GoCampaign_Precincts
 *      12/09/2019  NJN     Add ExportTypes, ExportTypeFilters, ExportJobs
 *      
 *      
 *  Copyright (c) 2018-2019 - Advarion Incorporated - All Rights Reserved.
 *  Unauthorized copying of this file via any medium is strictly prohibited.
 *  Proprietary and Confidential.
 *
 *******************************************************************************
 */

/*  Base Request Object */
import { requests } from './agents/requests';

/*  Authorization & Security */
import { Authorization, Roles } from './agents/agentSystemSecurity';

/*  Companies & Users */
import { Companies, CompanyStatuses } from './agents/agentCompanies';
import { Users, UserStatuses, UserRoles } from './agents/agentUsers';
import { CurrentUserProfile } from './agents/agentCurrentUser';

/*  System Components */
import { SystemDataTypes } from './agents/agentSystemDataTypes';
import { FileTypes } from './agents/agentFileTypes';
import {
    SystemListGroups, SystemLists, SystemListItems, SystemListItemPath, SystemListItemTree
} from './agents/agentSystemLists';
import { EmailSettings } from './agents/agentSystemSettings';
import {
    NavGroups, Navs, NavPath, NavTree, NavTreeForReactCheckboxTree, NavTreeRoles,
    RoleNavs, NavTypes, ComponentGroups, Components, ComponentPages
} from './agents/agentSystemNavigation';
import { 
    SystemBlockValidationTypes, BlockValidationTypeFields, BlockElementOptions, BlockElementTypes,
    BlockGroups, BlockGroupPath, BlockGroupTree, Blocks, BlockPath, BlockElements,
    BlockGroupTabGroups, BlockGroupTabs, BlockDatas, BlockTree, BlockFormats, BlockFormatLevels,
    BlockDataValues
} from './agents/agentSystemBlocks';
import {
    FieldSetGroups, FieldSetGroupPath, FieldSetGroupTree, FieldSets, Fields
} from './agents/agentFieldSetGroups';

/*  Advarion CMS */
import { Folders, Documents, Files, Images } from './agents/agentFolders';

/*  Advarion CRM */
import { Contacts } from './agents/agentContacts.js';
import { LocationStates, LocationCountries, LocationCounties, LocationZipCodes, LocationCities } from './agents/agentLocations.js';

/* Advarion Projects */
import { Project_Projects, Project_ProjectPath, Project_ProjectTree } from './agents/agentProject_Projects.js';
import { Project_ProjectVersions } from './agents/agentProject_ProjectVersions.js';
import { Project_ProjectJobs } from './agents/agentProject_ProjectJobs.js';
import { Project_ProjectJobAudits } from './agents/agentProject_ProjectJobAudits';
import { Project_ProjectJobHistories } from './agents/agentProject_ProjectJobHistories.js';

/* Gulf Crane Custom */
import { Consequences } from './agents/agentConsequences.js';
import { Probabilities } from './agents/agentProbabilities.js';
import { RiskClasses } from './agents/agentRiskClasses.js';
import { RiskMatrix } from './agents/agentRiskMatrix.js';
import { Assets } from './agents/agentAssets.js';
import { GCSEquipments, GCSEquipmentPath } from './agents/agentGCSEquipments.js';
import { GCSInspections } from './agents/agentGCSInspections.js';

/* GoCampaign */

import { GoCampaign_PoliticalDistrictGroups } from './agents/agentGoCampaign_PoliticalDistrictGroups.js';
import { GoCampaign_PoliticalDistricts } from './agents/agentGoCampaign_PoliticalDistricts.js';
import { GoCampaign_OfficeGroups } from './agents/agentGoCampaign_OfficeGroups.js';
import { GoCampaign_PrecinctGroups } from './agents/agentGoCampaign_PrecinctGroups.js';
import { GoCampaign_Precincts } from './agents/agentGoCampaign_Precincts.js';
import { GoCampaign_Counties } from './agents/agentGoCampaign_Counties.js';

/* Voter DB */
import { VoterDB_Voters } from './agents/agentVoterDB_Voters.js';
import { ExportTypes } from './agents/agentExportTypes.js';
import { ExportTypeFilters } from './agents/agentExportTypeFilters.js';
import { ExportJobs } from './agents/agentExportJobs.js';

export default {
/*  Base Request Object */
    requests,

/*  Authorization & Security */
    Authorization, Roles,

/*  Companies & Users */
    Companies, CompanyStatuses,
    Users, UserStatuses, UserRoles,
    CurrentUserProfile, 

/*  System Components */
    SystemDataTypes, FileTypes,
    SystemListGroups, SystemLists, SystemListItems, SystemListItemPath, SystemListItemTree, 
    EmailSettings,
    NavGroups, Navs, NavPath, NavTree, NavTreeForReactCheckboxTree, NavTreeRoles,
    RoleNavs, NavTypes, ComponentGroups, Components, ComponentPages,
    SystemBlockValidationTypes, BlockValidationTypeFields, BlockElementOptions, BlockElementTypes,
    BlockGroups, BlockGroupPath, BlockGroupTree, Blocks, BlockPath, BlockElements,
    BlockGroupTabGroups, BlockGroupTabs, BlockDatas, BlockTree, BlockFormats, BlockFormatLevels,
    BlockDataValues,
    FieldSetGroups, FieldSetGroupPath, FieldSetGroupTree, FieldSets, Fields,

/*  Advarion CMS */
    Folders, Documents, Files, Images,

/*  Advarion CRM */
    Contacts, LocationStates, LocationCountries, LocationCounties, LocationZipCodes, LocationCities, 

/*  Advarion Projects */
    Project_Projects, Project_ProjectPath, Project_ProjectTree, Project_ProjectVersions,
    Project_ProjectJobs, Project_ProjectJobAudits, Project_ProjectJobHistories,
   
/*  Gulf Crane Custom */
    Consequences, Probabilities, RiskClasses, RiskMatrix, Assets, GCSEquipments, GCSEquipmentPath, GCSInspections,

/*  GoCampaign */
    GoCampaign_PoliticalDistrictGroups, GoCampaign_PoliticalDistricts, GoCampaign_OfficeGroups, GoCampaign_PrecinctGroups,
    GoCampaign_Counties, GoCampaign_Precincts, 

/*  VoterDB */
    VoterDB_Voters, ExportTypes, ExportTypeFilters, ExportJobs,
};



// WEBPACK FOOTER //
// ./src/agent.js